<template>
  <div class="bulk-csv-upload">
    <div class="mb-6">
      <h4 class="text-lg font-medium text-slate-900 dark:text-white mb-2">
        {{ $t('BULK_BROADCAST.CSV_UPLOAD.TITLE') }}
      </h4>
      <p class="text-sm text-slate-600 dark:text-slate-400">
        {{ $t('BULK_BROADCAST.CSV_UPLOAD.DESCRIPTION') }}
      </p>
    </div>

    <!-- CSV Format Information -->
    <div class="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
      <div class="flex items-start space-x-3">
        <Icon icon="i-lucide-info" class="text-blue-500 mt-0.5" size="20" />
        <div class="flex-1">
          <h6 class="font-medium text-blue-900 dark:text-blue-100 mb-2">
            {{ $t('BULK_BROADCAST.CSV_UPLOAD.FORMAT_TITLE') }}
          </h6>
          <p class="text-sm text-blue-800 dark:text-blue-200 mb-3">
            {{ $t('BULK_BROADCAST.CSV_UPLOAD.FORMAT_DESCRIPTION') }}
          </p>

          <div class="bg-white dark:bg-slate-800 rounded-md p-3 mb-3">
            <div class="text-xs font-mono text-slate-700 dark:text-slate-300 font-medium mb-2">
              {{ $t('BULK_BROADCAST.CSV_UPLOAD.REQUIRED_COLUMNS') }}
            </div>
            <code class="text-xs text-slate-600 dark:text-slate-400 block">
              {{ csvFormatExample }}
            </code>
          </div>

          <div v-if="requiredVariables > 0" class="mb-3">
            <p class="text-sm text-blue-800 dark:text-blue-200">
              {{ $t('BULK_BROADCAST.CSV_UPLOAD.VARIABLES_REQUIRED', { count: requiredVariables }) }}
            </p>
            <div class="mt-1 flex flex-wrap gap-1">
              <span
                v-for="i in requiredVariables"
                :key="i"
                class="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300"
              >
                variable_{{ i }}
              </span>
            </div>
          </div>

          <p class="text-sm text-blue-800 dark:text-blue-200 mb-3">
            {{ $t('BULK_BROADCAST.CSV_UPLOAD.MAX_RECIPIENTS_NOTICE', { count: maxRecipientsLabel }) }}
          </p>

          <button
            @click="downloadExample"
            class="inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-700 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30"
          >
            <Icon icon="i-lucide-download" size="16" />
            <span>{{ $t('BULK_BROADCAST.CSV_UPLOAD.DOWNLOAD_EXAMPLE') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- File Upload Area -->
    <div class="mb-6">
      <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
        {{ $t('BULK_BROADCAST.CSV_UPLOAD.SELECT_FILE') }}
      </label>
      
      <div
        @drop="handleDrop"
        @dragover.prevent
        @dragenter.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        :class="[
          'border-2 border-dashed rounded-lg p-8 text-center transition-colors',
          isDragging
            ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
            : 'border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500'
        ]"
      >
        <input
          ref="fileInput"
          type="file"
          accept=".csv"
          @change="handleFileSelect"
          class="hidden"
        />
        
        <Icon icon="i-lucide-upload" class="text-slate-400 mb-3" size="48" />
        <p class="text-sm text-slate-600 dark:text-slate-400 mb-2">
          {{ $t('BULK_BROADCAST.CSV_UPLOAD.DRAG_DROP') }}
        </p>
        <button
          @click="$refs.fileInput.click()"
          class="inline-flex items-center space-x-2 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-700 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20"
        >
          <Icon icon="i-lucide-folder-open" size="16" />
          <span>{{ $t('BULK_BROADCAST.CSV_UPLOAD.SELECT_FILE_BUTTON') }}</span>
        </button>
      </div>
    </div>

    <!-- Processing State -->
    <div v-if="isProcessing" class="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
      <div class="flex items-center space-x-3">
        <Icon icon="i-lucide-loader-2" class="text-yellow-500 animate-spin" size="20" />
        <span class="text-sm font-medium text-yellow-800 dark:text-yellow-200">
          {{ $t('BULK_BROADCAST.CSV_UPLOAD.PROCESSING') }}
        </span>
      </div>
    </div>

    <!-- Errors -->
    <div v-if="csvErrors.length > 0" class="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
      <div class="flex items-start space-x-3">
        <Icon icon="i-lucide-alert-circle" class="text-red-500 mt-0.5" size="20" />
        <div class="flex-1">
          <h6 class="font-medium text-red-900 dark:text-red-100 mb-2">
            {{ $t('BULK_BROADCAST.CSV_UPLOAD.ERRORS_TITLE') }}
          </h6>
          <ul class="space-y-1">
            <li
              v-for="(error, index) in csvErrors.slice(0, 10)"
              :key="index"
              class="text-sm text-red-700 dark:text-red-300"
            >
              {{ error }}
            </li>
          </ul>
          <p v-if="csvErrors.length > 10" class="text-sm text-red-700 dark:text-red-300 mt-2">
            {{ $t('BULK_BROADCAST.CSV_UPLOAD.MORE_ERRORS', { count: csvErrors.length - 10 }) }}
          </p>
        </div>
      </div>
    </div>

    <!-- Duplicates Warning -->
    <div v-if="duplicatesWarning && duplicatesWarning.count > 0" class="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
      <div class="flex items-start space-x-3">
        <Icon icon="i-lucide-alert-triangle" class="text-amber-500 mt-0.5" size="20" />
        <div class="flex-1">
          <h6 class="font-medium text-amber-900 dark:text-amber-100 mb-2">
            {{ $t('BULK_BROADCAST.CSV_UPLOAD.DUPLICATES_TITLE') }}
          </h6>
          <p class="text-sm text-amber-700 dark:text-amber-300 mb-2">
            {{ $t('BULK_BROADCAST.CSV_UPLOAD.DUPLICATES_DESCRIPTION', { count: duplicatesWarning.count }) }}
          </p>
          <details class="text-sm">
            <summary class="cursor-pointer text-amber-800 dark:text-amber-200 font-medium hover:text-amber-900 dark:hover:text-amber-100">
              {{ $t('BULK_BROADCAST.CSV_UPLOAD.DUPLICATES_DETAILS_TOGGLE') }}
            </summary>
            <ul class="mt-2 space-y-1 ml-4 list-disc">
              <li
                v-for="(dup, index) in duplicatesWarning.details.slice(0, 20)"
                :key="index"
                class="text-amber-700 dark:text-amber-300"
              >
                {{ $t('BULK_BROADCAST.CSV_UPLOAD.DUPLICATES_DETAIL_ITEM', { phone: dup.phone, rows: dup.duplicateRows.join(', '), original: dup.originalRow }) }}
              </li>
            </ul>
            <p v-if="duplicatesWarning.details.length > 20" class="text-sm text-amber-700 dark:text-amber-300 mt-2 ml-4">
              {{ $t('BULK_BROADCAST.CSV_UPLOAD.DUPLICATES_MORE', { count: duplicatesWarning.details.length - 20 }) }}
            </p>
          </details>
        </div>
      </div>
    </div>

    <!-- Success State -->
    <div v-if="csvData.length > 0 && csvErrors.length === 0" class="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
      <div class="flex items-start space-x-3">
        <Icon icon="i-lucide-check-circle" class="text-green-500 mt-0.5" size="20" />
        <div>
          <h6 class="font-medium text-green-900 dark:text-green-100">
            {{ $t('BULK_BROADCAST.CSV_UPLOAD.SUCCESS_TITLE') }}
          </h6>
          <p class="mt-1 text-sm text-green-700 dark:text-green-300">
            {{ $t('BULK_BROADCAST.CSV_UPLOAD.SUCCESS_DESCRIPTION', { count: validRowsCount }) }}
          </p>
        </div>
      </div>
    </div>

    <!-- Preview Table -->
    <div v-if="csvData.length > 0" class="mt-6">
      <div class="flex items-center justify-between mb-4">
        <h6 class="font-medium text-slate-900 dark:text-white">
          {{ $t('BULK_BROADCAST.CSV_UPLOAD.PREVIEW_TITLE') }}
        </h6>
        <span class="text-sm text-slate-600 dark:text-slate-400">
          {{ $t('BULK_BROADCAST.CSV_UPLOAD.SHOWING_ROWS', { shown: Math.min(csvData.length, 20), total: csvData.length }) }}
        </span>
      </div>

      <div class="overflow-x-auto border border-slate-200 dark:border-slate-700 rounded-lg">
        <table class="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
          <thead class="bg-slate-50 dark:bg-slate-800">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                #
              </th>
              <th class="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                {{ $t('BULK_BROADCAST.CSV_UPLOAD.COLUMN_PHONE') }}
              </th>
              <th class="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                {{ $t('BULK_BROADCAST.CSV_UPLOAD.COLUMN_NAME') }}
              </th>
              <th
                v-for="i in requiredVariables"
                :key="`var_${i}`"
                class="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider"
              >
                variable_{{ i }}
              </th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-slate-900 divide-y divide-slate-200 dark:divide-slate-700">
            <tr
              v-for="(row, index) in csvData.slice(0, 20)"
              :key="index"
              :class="getRowClass(row, index)"
            >
              <td class="px-4 py-3 text-sm font-medium text-slate-900 dark:text-slate-100">
                {{ index + 1 }}
              </td>
              <td class="px-4 py-3 text-sm text-slate-900 dark:text-slate-100">
                {{ row.telefonos || '-' }}
              </td>
              <td class="px-4 py-3 text-sm text-slate-900 dark:text-slate-100">
                {{ row.nombres || '-' }}
              </td>
              <td
                v-for="i in requiredVariables"
                :key="`var_${index}_${i}`"
                class="px-4 py-3 text-sm text-slate-600 dark:text-slate-400"
              >
                {{ row[`variable_${i}`] || '-' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  csvData: {
    type: Array,
    default: () => []
  },
  csvErrors: {
    type: Array,
    default: () => []
  },
  requiredVariables: {
    type: Number,
    default: 0
  }
});

const emit = defineEmits([
  'update:csv-data',
  'update:csv-errors'
]);

const { t } = useI18n();

const MAX_RECIPIENTS = 5000;
const maxRecipientsLabel = computed(() => MAX_RECIPIENTS.toLocaleString());

const isDragging = ref(false);
const isProcessing = ref(false);
const duplicatesWarning = ref(null);

const csvFormatExample = computed(() => {
  const headers = ['telefonos', 'nombres'];
  for (let i = 1; i <= props.requiredVariables; i++) {
    headers.push(`variable_${i}`);
  }
  return headers.join(',');
});

const validRowsCount = computed(() => {
  return props.csvData.filter(row => 
    row.telefonos && 
    row.nombres && 
    (!props.requiredVariables || hasRequiredVariables(row))
  ).length;
});

const hasRequiredVariables = (row) => {
  for (let i = 1; i <= props.requiredVariables; i++) {
    if (!row[`variable_${i}`]) return false;
  }
  return true;
};

const handleDrop = (event) => {
  event.preventDefault();
  isDragging.value = false;
  
  const files = event.dataTransfer.files;
  if (files.length > 0) {
    processFile(files[0]);
  }
};

const handleFileSelect = (event) => {
  const file = event.target.files[0];
  if (file) {
    processFile(file);
  }
};

const processFile = async (file) => {
  
  if (!file.name.toLowerCase().endsWith('.csv')) {
    console.error('Invalid file type:', file.name);
    emit('update:csv-errors', [t('BULK_BROADCAST.CSV_UPLOAD.INVALID_FILE_TYPE')]);
    duplicatesWarning.value = null;
    return;
  }

  isProcessing.value = true;
  duplicatesWarning.value = null;
  const errors = [];
  const rows = [];

  try {
    const text = await file.text();
    const parsedData = parseCSV(text);
    
    if (parsedData.length < 2) {
      errors.push(t('BULK_BROADCAST.CSV_UPLOAD.EMPTY_FILE'));
      emit('update:csv-errors', errors);
      return;
    }

    const headers = parsedData[0].map(h => h.trim().toLowerCase());

    const requiredColumns = ['telefonos', 'nombres'];
    for (let i = 1; i <= props.requiredVariables; i++) {
      requiredColumns.push(`variable_${i}`);
    }

    const missingColumns = requiredColumns.filter(col => !headers.includes(col));
    if (missingColumns.length > 0) {
      errors.push(t('BULK_BROADCAST.CSV_UPLOAD.MISSING_COLUMNS', { columns: missingColumns.join(', ') }));
    }

    for (let i = 1; i < parsedData.length; i++) {
      const values = parsedData[i];
      const row = {};

      headers.forEach((header, index) => {
        row[header] = values[index] ? values[index].trim() : '';
      });

      if (Object.values(row).every(val => !val)) {
        continue;
      }

      // i + 1 = línea real del CSV, contando el encabezado
      const rowErrors = validateRow(row, i + 1);
      if (rowErrors.length > 0) {
        errors.push(...rowErrors);
      }

      // Normalizar a E.164 para el envío; si es inválido se deja el crudo
      // para que el error muestre lo que cargó el usuario.
      const normalizedPhone = normalizePhone(row.telefonos);
      if (normalizedPhone) {
        row.telefonos = normalizedPhone;
      }

      rows.push(row);
    }

    const { uniqueRows, duplicatesInfo } = removeDuplicates(rows);

    // Tope contado sobre destinatarios únicos (post dedup)
    if (uniqueRows.length > MAX_RECIPIENTS) {
      errors.unshift(
        t('BULK_BROADCAST.CSV_UPLOAD.MAX_RECIPIENTS_ERROR', {
          max: maxRecipientsLabel.value,
          count: uniqueRows.length.toLocaleString(),
        })
      );
    }

    if (duplicatesInfo.count > 0) {
      duplicatesWarning.value = duplicatesInfo;
    } else {
      duplicatesWarning.value = null;
    }

    emit('update:csv-data', uniqueRows);
    emit('update:csv-errors', errors);

  } catch (error) {
    console.error('Error processing CSV:', error);
    emit('update:csv-errors', [t('BULK_BROADCAST.CSV_UPLOAD.PROCESSING_ERROR')]);
  } finally {
    isProcessing.value = false;
  }
};

// Elimina duplicados por teléfono, conservando la primera aparición.
const removeDuplicates = (rows) => {
  const phoneMap = new Map();
  const uniqueRows = [];
  const duplicates = [];

  rows.forEach((row, index) => {
    const phone = row.telefonos ? row.telefonos.trim() : '';

    if (!phone) {
      // Filas sin teléfono se dejan pasar; las corta la validación.
      uniqueRows.push(row);
      return;
    }

    const normalizedPhone = phone.replace(/[\s\-\(\)\.]/g, '');

    if (!phoneMap.has(normalizedPhone)) {
      phoneMap.set(normalizedPhone, {
        phone: phone,
        originalRow: index + 2, // +2: fila 0 es el encabezado y el array es base 0
        duplicateRows: []
      });
      uniqueRows.push(row);
    } else {
      const existing = phoneMap.get(normalizedPhone);
      existing.duplicateRows.push(index + 2);
      phoneMap.set(normalizedPhone, existing);
    }
  });

  phoneMap.forEach((value, key) => {
    if (value.duplicateRows.length > 0) {
      duplicates.push(value);
    }
  });

  return {
    uniqueRows,
    duplicatesInfo: {
      count: duplicates.reduce((sum, dup) => sum + dup.duplicateRows.length, 0),
      details: duplicates
    }
  };
};

// Parser CSV que respeta comas y comillas escapadas ("") dentro de campos.
const parseCSV = (text) => {
  const rows = [];
  const lines = text.split(/\r?\n/);

  for (const line of lines) {
    if (!line.trim()) continue;

    const row = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        row.push(current);
        current = '';
      } else {
        current += char;
      }
    }

    row.push(current);
    rows.push(row);
  }

  return rows;
};

const validateRow = (row, rowNumber) => {
  const errors = [];
  const CSV = 'BULK_BROADCAST.CSV_UPLOAD';

  if (!row.telefonos) {
    errors.push(t(`${CSV}.MISSING_PHONE`, { row: rowNumber }));
  } else if (!isValidPhone(row.telefonos)) {
    errors.push(t(`${CSV}.INVALID_PHONE`, { row: rowNumber, phone: row.telefonos }));
  }

  if (!row.nombres) {
    errors.push(t(`${CSV}.MISSING_NAME`, { row: rowNumber }));
  }

  if (props.requiredVariables > 0) {
    for (let i = 1; i <= props.requiredVariables; i++) {
      const varName = `variable_${i}`;
      if (!row[varName]) {
        errors.push(t(`${CSV}.MISSING_VARIABLE`, { row: rowNumber, variable: varName }));
      }
    }
  }

  return errors;
};

const DEFAULT_COUNTRY = 'AR';

// Normaliza a E.164 (ej: +541123456789), o null si es inválido.
// Con "+" respeta el país del número; sin "+" asume DEFAULT_COUNTRY.
const normalizePhone = (phone) => {
  if (!phone) return null;

  const clean = phone.replace(/[\s\-().]/g, '');
  const country = clean.startsWith('+') ? undefined : DEFAULT_COUNTRY;

  try {
    if (!isValidPhoneNumber(clean, country)) return null;
    return parsePhoneNumber(clean, country).number;
  } catch {
    return null;
  }
};

const isValidPhone = (phone) => normalizePhone(phone) !== null;

const getRowClass = (row, index) => {
  const hasErrors = !row.telefonos || !row.nombres || (props.requiredVariables && !hasRequiredVariables(row));
  
  return hasErrors
    ? 'bg-red-50 dark:bg-red-900/10'
    : index % 2 === 0
    ? 'bg-white dark:bg-slate-900'
    : 'bg-slate-50 dark:bg-slate-800/50';
};

const downloadExample = () => {
  const headers = ['telefonos', 'nombres'];
  for (let i = 1; i <= props.requiredVariables; i++) {
    headers.push(`variable_${i}`);
  }
  
  const examples = [
    ['+5491123456789', 'Juan Pérez', ...Array.from({ length: props.requiredVariables }, (_, i) => `Valor${i + 1}`)],
    ['+5491187654321', 'María García', ...Array.from({ length: props.requiredVariables }, (_, i) => `Ejemplo${String.fromCharCode(65 + i)}`)],
    ['1156789012', 'Carlos López', ...Array.from({ length: props.requiredVariables }, (_, i) => `Test${i + 1}`)],
    ['+34612345678', 'Ana Martín', ...Array.from({ length: props.requiredVariables }, (_, i) => `Dato${i + 1}`)],
    ['5493456789012', 'Luis Rodriguez', ...Array.from({ length: props.requiredVariables }, (_, i) => `Info${i + 1}`)]
  ];

  // Escapar valores que contengan comas o comillas
  const escapeCsvValue = (value) => {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  const csvContent = [
    headers.join(','), 
    ...examples.map(row => row.map(escapeCsvValue).join(','))
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'bulk_broadcast_example.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};
</script>